import { useEffect, useState } from 'react';
import { useStrings } from '../../state/useLocale';
import type { CheckpointCheck } from '../../state/useCheckpoint';
import { Button } from '../../ui';

/**
 * What "Check my work" has to say on a checkpoint that the objectives cannot.
 *
 * Assessment uses the same Check button (state/useCheckpoint.ts). This is the
 * status text and connection-retry action that only
 * a checkpoint needs, under the objectives where the learner is already
 * looking: that a check is running and takes a while, why the grader refused
 * the work before looking at it, why no verdict came back, and how much of
 * the hour's allowance is spent. Each one is there because it changes what the
 * learner does next. It is empty when none of them applies, which is most of
 * the time.
 */
export function CheckpointStatus({ check }: { check: CheckpointCheck }) {
  const s = useStrings().checkpoint;
  const { checking, assessmentStatus, earlierAssessment, stale, output, problem, limits, verdict, passed } = check;
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (problem !== 'quota') return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [problem, limits?.nextEligibleAt]);

  const lines: string[] = [];
  if (checking) lines.push(s.checkingProgress);
  if (assessmentStatus === 'checking') lines.push(s.checking);
  if (assessmentStatus === 'waiting') lines.push(s.stillChecking);
  if (assessmentStatus === 'reconnecting') lines.push(s.reconnecting);
  if (assessmentStatus === 'limited') lines.push(s.waitingAllowance);
  if (problem === 'quota' && limits?.nextEligibleAt) {
    lines.push(s.waitFor(Math.max(0, Math.ceil((limits.nextEligibleAt - now) / 60_000))));
  } else if (problem) lines.push(s[problem]);
  if (!checking && verdict) for (const gate of verdict.gates) lines.push(s.gates[gate]);
  if (!checking && verdict?.grade?.practice && !problem && assessmentStatus === 'idle') lines.push(s.progressOnly);
  if (stale) lines.push(s.earlierWork);
  if (passed) lines.push(s.badgeEarned);
  // The allowance once some of it is spent, and never beside a pass: a learner
  // who has finished does not need to budget.
  const allowance = !passed && !checking && problem !== 'quota' && problem !== 'paused'
    && limits && limits.hour.used > 0 ? s.allowance(limits.hour.used, limits.hour.limit) : null;

  // The live region is always mounted and only its contents come and go: a
  // region that appears already holding its sentence is not reliably read out.
  return (
    <div className="checkpoint-status" role="status" aria-live="polite">
      {lines.length > 0 && <p>{lines.join(' ')}</p>}
      {(assessmentStatus === 'reconnecting' || assessmentStatus === 'waiting') && <Button type="button" onClick={check.retryAssessment}>{s.retryConnection}</Button>}
      {allowance && <p className="note">{allowance}</p>}
      {earlierAssessment && <details>
        <summary>{earlierAssessment.result.passed ? s.earlierPassed : s.earlierFailed}</summary>
        <p>{s.earlierDetail}</p>
        {earlierAssessment.gates.map(gate => <p key={gate}>{s.gates[gate]}</p>)}
        <ul>{earlierAssessment.result.objectives.filter(o => !o.passed && !o.skipped).map(o =>
          <li key={o.id}>{o.title}{o.message && <p>{o.message}</p>}</li>)}</ul>
      </details>}
      {output && <details><summary>{s.testOutput}</summary><pre dir="ltr" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{output}</pre></details>}
    </div>
  );
}
